import { useParams } from "react-router";
import { path } from "@/utils/url";
import { useCurrentUrl } from "@shared-ui/hooks/useCurrentUrl";
import { useTenantBranding } from "../hooks/useTenantBranding";
import { resolveLoginTenant } from "../loginTenant";

/**
 * ログインページ共通レイアウト（ロゴ + センタリング外枠）
 *
 * PRODUCT_DEV-570: 各プロダクトから `?productId=xxx` 付きで遷移してくるため、
 * productId=unizo の場合のみ Unizo ロゴを表示する（それ以外は umeee 共通ロゴ）。
 * 呼び出し側 (LoginForm / MFA / パスワード系など多数) を変更せずに済むよう、
 * ここで URL クエリの productId を直接参照する。
 *
 * PRODUCT_DEV-750: Minizo（メモ・ToDo だけのデモ版・PRODUCT_DEV-688）を追加。
 * Minizo は Unizo と同じ Cognito・同じ Portal でログインする（アップグレード時に
 * アカウントを作り直させないため）ので、ここで出し分ける必要がある。
 * ロゴは正式版が無いため暫定の SVG ワードマーク。
 */
const resolveBrandLogo = (): { src: string; alt: string } => {
  const productId =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("productId")
      : null;
  if (productId === "unizo") {
    return { src: path("/img/icon/unizo_logo_full.png"), alt: "Unizo" };
  }
  if (productId === "minizo") {
    return { src: path("/img/icon/minizo_logo_full.svg"), alt: "Minizo" };
  }
  return { src: path("/img/icon/umeee_logo_full.webp"), alt: "Umeee" };
};

export const AuthPageLayout: React.FC<{
  children: React.ReactNode;
  /**
   * PRODUCT_DEV-641: テナントのブランドロゴを製品ロゴと併記するか。
   *
   * **既定 false。** この外枠はパスワード変更・セキュリティ設定・MFA など
   * 11 箇所から使われており、無条件に出すと「ログイン画面だけ」という要件を破る。
   * 渡すのは `LoginForm` だけ。
   */
  showTenantBrand?: boolean;
}> = ({ children, showTenantBrand = false }) => {
  const logo = resolveBrandLogo();
  const params = useParams<{ tenantId?: string }>();
  const { query } = useCurrentUrl();

  /**
   * ブランドを出す相手のテナント。**確定していなければ null**（＝API を呼ばない）。
   * 出どころは固定ドメイン（PRODUCT_DEV-641）／パス `/:tenantId`／`?tenant=` の3つで、
   * `LoginPage` の `aliasTenantId` / `fixedTenantId` と同じ並び。
   */
  const brandTenantId = showTenantBrand
    ? resolveLoginTenant(params.tenantId, query.get("tenant")).tenantId
    : null;
  const { logoUrl: tenantLogoUrl } = useTenantBranding(brandTenantId);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/*
          ロゴが1枚だけのときは中央に1枚が置かれるだけで、**未設定のテナントでは
          従来と同じ見た目になる**（テナントロゴ用の領域を先に確保しない）。
          横幅は左右とも上限を切る。ロゴの横幅は入稿次第でばらつくので、
          片方が長いと相手を押し出してしまう。

          PRODUCT_DEV-1100: テナントのロゴがあるときは**お客さまのロゴを左**に置き、
          高さは Umeee のロゴ（3rem）より少し大きい 3.25rem にする。横書きでは左が先に目に入る
          上位の位置で、行員が使うお客さまの画面なので、お客さまを先に立てる（2026-10-09 決定）。
        */}
        <div className="mb-12 flex items-center justify-center gap-4">
          {tenantLogoUrl && (
            <>
              {/*
                alt はテナント名にしない。テナント名は未認証では引けない値で、
                引けるようにすると tenantId からテナント名を列挙できてしまう。
              */}
              <img
                src={tenantLogoUrl}
                alt="テナントのロゴ"
                className="h-[3.25rem] w-auto max-w-[45%] object-contain"
              />
              <span
                aria-hidden="true"
                className="h-8 w-px shrink-0 bg-neutral-200"
              />
            </>
          )}
          <img
            src={logo.src}
            alt={logo.alt}
            className="h-12 w-auto max-w-[45%] object-contain"
          />
        </div>
        {children}
      </div>
    </div>
  );
};
